import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { msalInstance } from "@/main";
import type { AccountInfo } from "@azure/msal-browser";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";
const RETRIED = "x-retried-once";

const api = axios.create({ baseURL: apiUrl });

// Send the user to the gated /login page instead of auto-triggering loginRedirect.
// Auto loginRedirect from an interceptor races the MSAL redirect handshake and loops
// (the RETRIED flag lives in request-config memory and is lost on every reload).
let isRedirectingToLogin = false;
async function redirectToLogin() {
  if (isRedirectingToLogin || window.location.pathname === "/login") return;
  isRedirectingToLogin = true;
  try {
    await msalInstance.clearCache();
  } finally {
    window.location.assign("/login");
  }
}

async function acquireTokens(forceRefresh = false) {
  const account: AccountInfo | null =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;

  if (!account && !forceRefresh) {
    throw new Error("No authenticated account found");
  }

  const request = {
    scopes: ["User.Read"],
    ...(account ? { account } : {}),
    forceRefresh,
  };

  const res = await msalInstance.acquireTokenSilent(request);
  return { accessToken: res.accessToken, idToken: res.idToken };
}

// Request: attach tokens. Never navigate here — let the router guard handle auth.
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (window.location.pathname === "/login") {
      return Promise.reject(new Error("Not authenticated"));
    }

    try {
      const tokens = await acquireTokens(false);
      if (config.headers) {
        config.headers["Access-Token"] = tokens.accessToken;
        config.headers["Id-Token"] = tokens.idToken;
      }
    } catch {
      await redirectToLogin();
      return Promise.reject(new Error("Authentication required"));
    }
    return config;
  },
  (error) => {
    console.error("Error Request: ", error);
    return Promise.reject(error);
  }
);

// Response: on 401 try one silent refresh, then hand off to the gated login page.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as InternalAxiosRequestConfig & {
      [RETRIED]?: boolean;
    };

    if (status === 401 && original && !original[RETRIED]) {
      try {
        original[RETRIED] = true;
        const tokens = await acquireTokens(true);
        if (original.headers) {
          original.headers["Access-Token"] = tokens.accessToken;
          original.headers["Id-Token"] = tokens.idToken;
        }
        return api.request(original);
      } catch {
        await redirectToLogin();
        return Promise.reject(new Error("Session expired, please login again"));
      }
    }

    return Promise.reject(error);
  }
);

export default api;
